import { Injectable } from '@angular/core';
import { Candidates } from '@interfaces/candidates.model';
import { dropdownValues } from '@interfaces/common.model';
import { filterData, formatSingleDate } from '@utils/utils';
import { yearDateFormat } from '@constants/dateFormat';
import { CANDIDATE_FILTER_CONSTANTS } from '@constants/candidate-filter.constants';

export interface FilterCriteria {
  candidateName?: string | null;
  yearsOfExperience?: string | null;
  location?: string | null;
  status?: string | string[] | null;
  earliestAvailability?: string | null;
  createdDate?: string | null;
  hiringManagerName?: string | null;
  jobTitle?: string | null;
  vendorName?: string | null;
}

export interface FilterOptions {
  name: dropdownValues[];
  yearsOfExperience: dropdownValues[];
  location: dropdownValues[];
  status: dropdownValues[];
  hiringManager: dropdownValues[];
  position: dropdownValues[];
  vendor: dropdownValues[];
}

@Injectable({
  providedIn: 'root'
})
export class CandidateFilterService {

  constructor() { }

  extractUniqueValues(candidates: Candidates[], field: string): string[] {
    const candidateList = candidates?.length > 0 && candidates[0]?.data ? 
      candidates.flatMap(candidate => candidate.data) : 
      candidates;
      
    const values = [...new Set(candidateList?.map((item: any) => {
      const value = item[field];
      if (value === null || value === undefined || value === '') {
        return null;
      }
      
      if (field === 'yearsOfExperience') {
        if (typeof value === 'string') {
          const numericValue = value.replace(/[^\d.]/g, '');
          return numericValue ? `${numericValue}` : value;
        }
        return value.toString();
      }
      
      return value;
    }))]?.filter(
      (value) => value !== null && value !== '' && value !== undefined,
    );
    return values || [];
  }

  generateDropdownOptions(values: string[]): dropdownValues[] {
    return values?.map((value) => ({
      label: value,
      value,
    })) || [];
  }

  generateFilterOptions(candidates: Candidates[]): FilterOptions {
    return {
      name: this.generateDropdownOptions(this.extractUniqueValues(candidates, 'candidateName')),
      yearsOfExperience: this.generateDropdownOptions(this.extractUniqueValues(candidates, 'yearsOfExperience')),
      location: this.generateDropdownOptions(this.extractUniqueValues(candidates, 'location')),
      status: this.generateDropdownOptions(this.extractUniqueValues(candidates, 'status')),
      hiringManager: this.generateDropdownOptions(this.extractUniqueValues(candidates, 'hiringManagerName')),
      position: this.generateDropdownOptions(this.extractUniqueValues(candidates, 'jobTitle')),
      vendor: this.generateDropdownOptions(this.extractUniqueValues(candidates, 'vendorName'))
    };
  }

  convertFormToFilterCriteria(formValue: any): FilterCriteria {
    const getFilterValue = (field: string, isMultiSelect: boolean = false) => {
      const value = formValue[field];
      if (!value) return null;
      
      if (typeof value === 'object' && value.value) {
        return value.value;
      }
      
      if (Array.isArray(value)) {
        if (isMultiSelect) {
          const selectedItems = value.filter(
            item => typeof item === 'string' || typeof item === 'number' || item?.selected === true
          );
          return selectedItems.length > 0
            ? selectedItems.map(item => (typeof item === 'object' ? item.value : item))
            : null;
        } else {
          const selectedItem = value.find((item) => item?.selected === true);
          return selectedItem ? selectedItem.value : null;
        }
      }
      
      return value;
    };
    
    const formControls = CANDIDATE_FILTER_CONSTANTS.FORM_CONTROLS;

    const criteria = {
      candidateName: getFilterValue(formControls.SELECTED_NAME),
      yearsOfExperience: getFilterValue(formControls.SELECTED_EXPERIENCE),
      location: getFilterValue(formControls.SELECTED_LOCATION),
      status: getFilterValue(formControls.SELECTED_STATUS, true),
      earliestAvailability: getFilterValue(formControls.SELECTED_AVAILABLE_BY)
        ? formatSingleDate(getFilterValue(formControls.SELECTED_AVAILABLE_BY), yearDateFormat.date)
        : null,
      createdDate: getFilterValue(formControls.SELECTED_DATE_APPLIED)
        ? formatSingleDate(getFilterValue(formControls.SELECTED_DATE_APPLIED), yearDateFormat.date)
        : null,
      hiringManagerName: getFilterValue(formControls.SELECTED_HIRING_MANAGER),
      jobTitle: getFilterValue(formControls.SELECTED_POSITION),
      vendorName: getFilterValue(formControls.SELECTED_VENDOR),
    };
    return criteria;
  }

  filterCandidates(
    candidates: Candidates[], 
    filters: FilterCriteria, 
    excludeKeys: string[] = []
  ): Candidates[] {
    if (!filters || typeof filters !== 'object') {
      return candidates;
    }
    const filterCriteria = { ...filters } as any;
    excludeKeys.forEach((key) => delete filterCriteria[key]);
    const candidateList = candidates?.length > 0 && candidates[0]?.data ? 
      candidates.flatMap(candidate => candidate.data) : 
      candidates;
    
    const filtered = candidateList.filter(candidate => {
      return Object.entries(filterCriteria).every(([key, value]) => {
        if (!value) return true;     
        const candidateValue = (candidate as any)[key];      
        if (key === 'status' && Array.isArray(value)) {
          return value.includes(candidateValue);
        }        
        if (key === 'status' && typeof value === 'string') {
          return candidateValue === value;
        }      
        if ((key === 'earliestAvailability' || key === 'createdDate') && typeof value === 'string') {
          if (!candidateValue) return false;    
          const filterDate = new Date(value);
          let candidateDate: Date;    
          if (typeof candidateValue === 'string') {
            candidateDate = new Date(candidateValue);
          } else if (candidateValue instanceof Date) {
            candidateDate = candidateValue;
          } else {
            return false;
          }
          
          if (isNaN(filterDate.getTime()) || isNaN(candidateDate.getTime())) {
            return false;
          }
          
          const normalizedFilterDate = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
          const normalizedCandidateDate = new Date(candidateDate.getFullYear(), candidateDate.getMonth(), candidateDate.getDate());  
          return normalizedCandidateDate.getTime() === normalizedFilterDate.getTime();
        }
        
        if (typeof value === 'string') {
          return candidateValue?.toString().toLowerCase() === value.toLowerCase();
        }     
        return candidateValue === value;
      });
    });
    
    return filtered;
  }

  hasFilterValues(filterCriteria: FilterCriteria): boolean {
    return Object.values(filterCriteria).some(value => 
      value !== null && value !== undefined && value !== ''
    );
  }
}
